import { PayloadDto } from '@stores/payloads';

export interface AgentVersionHistoryDto {
  agent: AgentDto;
  agentInputs: AgentInputDto[];
  agentPrompts: AgentPromptDto[];
  agentInstructions: AgentInstructionDto[];
}

export interface AgentDto {
  agentUuid: string;
  tenantUuid: string;
  subscriptionUuid: string;
  entityUuid?: string;
  entityUuids?: string[];
  agentName: string;
  agentStatusType: AgentStatusType;
  agentDescription: string;
  outputTypeId: number;
  outputDescription: string;
  outputSwagger: string;
  widgetUuid: string;
  modelUuid: string;
  agentAccountEmail: string;
  priorityLevel: number;
  createdAt: string;
  updatedAt: string;
  temperature: number;
  lastActive?: string;
  lastStatus: AgentStatusType;
  versionId?: number;
  agentSchemaVersion?: string;
  deleted: boolean;
  webSearch: number;
  agentHub: string;
}

export interface AgentInstructionDto {
  agentInstructionId: number;
  tenantUuid: string;
  agentUuid: string;
  title: string;
  body: string;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  file: AgentInstructionFile;
}

export interface AgentInstructionFile {
  fileUuid: string;
  fileName: string;
  storageFileName: string;
  contentType: string;
  size: number;
  url: AgentInstructionFileUrl;
  status: string;
  variantCount: number;
  pageCount: number;
  variants: string[];
  pages: string[];
  isDuplicated: boolean;
  isRejected: boolean;
  reason: string;
}

export interface AgentInstructionFileUrl {
  url: string;
  method: string;
  storageType: string;
  type: string;
}

export interface AgentPromptDto {
  agentPromptId: number;
  tenantUuid: string;
  agentUuid: string;
  prompt: string;
  createdAt: string;
  updatedAt: string;
}

export interface AgentInputDto {
  agentInputId: number;
  tenantUuid: string;
  agentUuid: string;
  inputName: string;
  inputDescription: string;
  priority: number;
  payload: PayloadDto;
  createdAt: string;
  updatedAt: string;
}

export interface AgentVersionDto {
  agentVersionUuid: string;
  tenantId: number;
  agentVersionId: number;
  agentUuid: string;
  elementType: string;
  agentSchemaVersion: string;
  agentModel: AgentModel;
  agentContext: AgentContext;
  agentSystemPrompt: AgentSystemPrompt;
  online: boolean;
  onlineAt: string;
  offlineAt: string;
  createdAt: string;
  updatedAt: string;
  agentVersionStatusId: number;
  agentVersionStatus: AgentStatusType;
  agentVersionNumber: number;
}

export interface AgentModel {
  maxTokens: number;
  temperature: number;
  aiModelName: string;
}

export interface AgentContext {
  agentName: string;
  agentDescription: string;
}

export interface AgentSystemPrompt {
  examples: string[];
  instructions: Instruction[];
  prompts: PromptDto[];
}

export interface Instruction {
  body: string;
  title: string;
  displayOrder: number;
}

export interface PromptDto {
  prompt: string;
}

export interface CreateUpdateAgentPromptPayload {
  prompt: string;
}

export interface CreateUpdateAgentInputPayload {
  priority: number;
  payloadId: number;
}

export interface CreateUpdateAgentInstructionPayload {
  title: string;
  body: string;
  displayOrder: number;
  deleteFile?: boolean;
  file?: {
    file?: File;
    fileName: string;
    contentType: string;
    size: number;
    resumable: boolean;
  };
  currentFile?: AgentInstructionFile;
}

export interface CreateAgentPayload {
  subscriptionUuid: string;
  entityUuids: string[];
  agentName: string;
  agentDescription: string;
  agentHub: string;
  modelUuid: string;
  priorityLevel: number;
  outputTypeId?: number;
  outputDescription?: string;
  outputSwagger?: string;
  widgetUuid?: string;
  agentAccountEmail?: string;
  temperature: number;
  webSearch: number;
  agentStatusType: AgentStatusType;
}

export interface UpdateAgentPayload {
  subscriptionUuid: string;
  entityUuids: string[];
  agentName: string;
  agentStatusType: AgentStatusType;
  agentDescription: string;
  agentHub: string;
  outputTypeId: number;
  outputDescription: string;
  outputSwagger: string;
  widgetUuid: string;
  modelUuid: string;
  agentAccountEmail: string;
  priorityLevel: number;
  temperature?: number;
  webSearch: number;
}

export interface UpdateAgentVersionPayload {
  tenantUuid: string;
  agentUuid: string;
  versionId: number;
  status: AgentStatusType;
}

export interface AgentHub {}

export enum AgentStatusType {
  CREATING = 'CREATING',
  TEST_OFFLINE = 'TEST_OFFLINE',
  TEST_ONLINE = 'TEST_ONLINE',
  PUBLISHED_ONLINE = 'PUBLISHED_ONLINE',
  PUBLISHED_OFFLINE = 'PUBLISHED_OFFLINE',
  DELETED = 'DELETED',
}

export enum WebSearchType {
  NONE,
  SIMPLE_SEARCH,
  DEEP_SEARCH,
}

export type StatusControl = 'test' | 'publish';
export type OnlineToggle = boolean;

export const STATUS_LABELS: Record<AgentStatusType, string> = {
  [AgentStatusType.CREATING]: 'Creating',
  [AgentStatusType.TEST_ONLINE]: 'Test',
  [AgentStatusType.TEST_OFFLINE]: 'Test',
  [AgentStatusType.PUBLISHED_ONLINE]: 'Validated',
  [AgentStatusType.PUBLISHED_OFFLINE]: 'Validated',
  [AgentStatusType.DELETED]: 'Deleted',
};

export const STATUS_COLORS: Record<AgentStatusType, string> = {
  [AgentStatusType.CREATING]: 'text-[var(--mtm-warning-text)]',
  [AgentStatusType.TEST_ONLINE]: 'text-[var(--mtm-info-text)]',
  [AgentStatusType.TEST_OFFLINE]: 'text-[var(--mtm-text-muted)]',
  [AgentStatusType.PUBLISHED_ONLINE]: 'text-[var(--mtm-success-text)]',
  [AgentStatusType.PUBLISHED_OFFLINE]: 'text-[var(--mtm-text-muted)]',
  [AgentStatusType.DELETED]: 'text-[var(--mtm-danger-text)]',
};

export const WEB_SEARCH_LABEL: Record<WebSearchType, string> = {
  [WebSearchType.NONE]: 'None',
  [WebSearchType.SIMPLE_SEARCH]: 'Simple Search',
  [WebSearchType.DEEP_SEARCH]: 'Deep Search',
};

export function getStatusColor(status: AgentStatusType | undefined): string {
  if (status === undefined || status === null) return 'text-[var(--mtm-text-muted)]';
  return STATUS_COLORS[status] || 'text-[var(--mtm-text-muted)]';
}

export function getStatusLabel(status: AgentStatusType | undefined): string {
  if (status === undefined || status === null) return 'Test';
  return STATUS_LABELS[status] || 'Test';
}

export function isPublishedStatus(status: AgentStatusType | undefined): boolean {
  return (
    status === AgentStatusType.PUBLISHED_ONLINE || status === AgentStatusType.PUBLISHED_OFFLINE
  );
}

export function combineStatus(
  statusControl: StatusControl,
  isOnline: OnlineToggle,
): AgentStatusType {
  return statusControl === 'test'
    ? isOnline
      ? AgentStatusType.TEST_ONLINE
      : AgentStatusType.TEST_OFFLINE
    : isOnline
      ? AgentStatusType.PUBLISHED_ONLINE
      : AgentStatusType.PUBLISHED_OFFLINE;
}

export function getStatusControl(agentStatus: AgentStatusType): StatusControl {
  switch (agentStatus) {
    case AgentStatusType.TEST_ONLINE:
    case AgentStatusType.TEST_OFFLINE:
      return 'test';
    case AgentStatusType.PUBLISHED_ONLINE:
    case AgentStatusType.PUBLISHED_OFFLINE:
      return 'publish';
    default:
      return 'test';
  }
}

export function getOnlineStatus(agentStatus: AgentStatusType): OnlineToggle {
  switch (agentStatus) {
    case AgentStatusType.TEST_ONLINE:
    case AgentStatusType.PUBLISHED_ONLINE:
      return true;
    case AgentStatusType.TEST_OFFLINE:
    case AgentStatusType.PUBLISHED_OFFLINE:
    case AgentStatusType.CREATING:
    case AgentStatusType.DELETED:
      return false;
    default:
      return false;
  }
}

export function reverseCombineStatus(
  currentStatus: AgentStatusType,
  newIsOnline: boolean,
): AgentStatusType {
  const isTestStatus =
    currentStatus === AgentStatusType.TEST_ONLINE || currentStatus === AgentStatusType.TEST_OFFLINE;
  if (isTestStatus) {
    return newIsOnline ? AgentStatusType.TEST_ONLINE : AgentStatusType.TEST_OFFLINE;
  } else {
    return newIsOnline ? AgentStatusType.PUBLISHED_ONLINE : AgentStatusType.PUBLISHED_OFFLINE;
  }
}
